import React, { useMemo } from "react";
import { AquariusBadge } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { LoyaltyStoreAccess } from "../../store";
import { LoyaltyApi } from "@smartrr/shared/interfaces/loyalty/api";
import { LightTooltip } from "@smartrr/shared/customerPortal/components/AquariusToolTip";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";

interface RewardCostBadgeProps {
  /** Cost to display */
  costInPoints: number;
  /** ORM ID for reward incentive or variant */
  rewardItemId: string;
}

interface TierRewardCostBadgeWithTooltipProps {
  allTiers: LoyaltyApi.Tier.Type[];
  tier: LoyaltyApi.Tier.Type;
  costInPoints: number;
}

interface TierColorIndicatorProps {
  allTiers: LoyaltyApi.Tier.Type[];
  tier: LoyaltyApi.Tier.Type;
}

const TierColorIndicator = ({ allTiers, tier }: TierColorIndicatorProps) => {
  if (!tier || !allTiers.length) {
    return <React.Fragment />;
  }

  const isLowestTier = allTiers[0].minimumPoints === 0 && tier.id === allTiers[0].id;

  // We don't want to surface the badge if reward item is on lowest - all customers have access to lowest item
  if (isLowestTier) {
    return <React.Fragment />;
  }

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
      <circle cx="6.5" cy="6" r="6" fill={tier.tierColor} />
    </svg>
  );
};

export const RewardCostBadge = ({ rewardItemId, costInPoints }: RewardCostBadgeProps) => {
  const { allTiers } = LoyaltyStoreAccess.useTiers();
  const tiersContainingReward: LoyaltyApi.Tier.Type[] | undefined =
    LoyaltyStoreAccess.useTiersContainingReward(rewardItemId);
  const tier = tiersContainingReward ? tiersContainingReward[0] : undefined;

  if (tier && allTiers.length) {
    return <TierRewardCostBadgeWithTooltip allTiers={allTiers} tier={tier} costInPoints={costInPoints} />;
  }

  return (
    <AquariusBadge
      label={costInPoints.toLocaleString(navigator.language).replaceAll(/\s/g, "")}
      icon="trophy"
      sx={{ width: "max-content", minWidth: "61px", margin: "0 auto !important", gap: "4px" }}
      className={prefixClassName("loyalty-reward-cost-badge")}
    />
  );
};

export const TierRewardCostBadgeWithTooltip = ({
  allTiers,
  tier,
  costInPoints,
}: TierRewardCostBadgeWithTooltipProps) => {
  const isHighestTier = useMemo(() => allTiers[allTiers.length - 1].id === tier.id, [allTiers]);
  const isLowestTier = useMemo(() => allTiers[0].minimumPoints === 0 && tier.id === allTiers[0].id, [allTiers]);

  if (isLowestTier) {
    return (
      <AquariusBadge
        label={costInPoints.toLocaleString(navigator.language).replaceAll(/\s/g, "")}
        icon="trophy"
        sx={{ width: "max-content", minWidth: "61px", margin: "0 auto !important", gap: "4px" }}
        className={prefixClassName("loyalty-reward-cost-badge")}
        suffixIcon={<TierColorIndicator allTiers={allTiers} tier={tier} />}
      />
    );
  }
  return (
    <LightTooltip
      title={typedLocalize(
        isHighestTier ? "loyalty.tiers.tierExclusiveTooltip" : "loyalty.tiers.tierExclusiveTooltip_andUp",
        {
          tierName: tier.tierName,
        }
      )}
      placement="top"
    >
      <span style={{ display: "inline-flex" }} data-testid="reward-badge-tier-indicator">
        <AquariusBadge
          label={costInPoints.toLocaleString(navigator.language).replaceAll(/\s/g, "")}
          icon="trophy"
          sx={{ width: "max-content", minWidth: "61px", margin: "0 auto !important", gap: "4px" }}
          className={prefixClassName("loyalty-reward-cost-badge")}
          suffixIcon={<TierColorIndicator allTiers={allTiers} tier={tier} />}
        />
      </span>
    </LightTooltip>
  );
};
